import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { contactAPI } from '../api/api';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors } from '../theme';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await contactAPI.sendEmail({
        name: formData.name,
        company: formData.company || undefined,
        email: formData.email,
        phone: formData.phone || undefined,
        projectType: formData.projectType || undefined,
        message: formData.message,
      });
      setSnackbar({
        open: true,
        message: "Thank you! We'll be in touch within 24 hours on most quotes.",
        severity: 'success',
      });
      setFormData({ name: '', company: '', email: '', phone: '', projectType: '', message: '' });
    } catch {
      setSnackbar({
        open: true,
        message: 'Something went wrong. Please try again or email us directly.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const whyChooseUs = [
    'Free initial consultation',
    'Detailed project estimates',
    'Licensed & insured',
    '25+ years experience',
    '24/7 emergency support',
    '24 business hour quote turnaround',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, rgba(245, 166, 35, 0.1) 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, mb: 1, display: 'block' }}
            >
              Get In Touch
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Request For Quote
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: colors.primary,
                fontWeight: 600,
              }}
            >
              24 business hour quote turnaround
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }} />

      {/* Contact Form Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Left side - Form */}
            <Grid item xs={12} md={8}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Send Us a Message
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 4,
                  mt: 1,
                }}
              >
                Email Us
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name *"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address *"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Type (e.g., Control System, Panel Fabrication, Automation)"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Project Details *"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={5}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: colors.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.primary,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        px: 5,
                        py: 1.5,
                      }}
                    >
                      {submitting ? 'Sending...' : 'Submit Request'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Right side - Contact Info Card */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: colors.border,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                  position: 'sticky',
                  top: 100,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 4,
                      pb: 3,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: 'rgba(245, 166, 35, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <LocationOnIcon sx={{ color: colors.primary, fontSize: 24 }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 700,
                      }}
                    >
                      Main Office
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PhoneIcon sx={{ color: colors.primary, fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, mb: 0.5 }}>
                          Telephone
                        </Typography>
                        <Typography
                          component="a"
                          href="tel:3154797262"
                          sx={{
                            color: colors.secondary,
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { color: colors.primary },
                          }}
                        >
                          315-479-7262
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <EmailIcon sx={{ color: colors.primary, fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, mb: 0.5 }}>
                          Email
                        </Typography>
                        <Typography
                          component="a"
                          href="mailto:jsmith@processcontrolllc.com.com"
                          sx={{
                            color: colors.secondary,
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { color: colors.primary },
                          }}
                        >
                          jsmith@processcontrolllc.com
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AccessTimeIcon sx={{ color: colors.primary, fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, mb: 0.5 }}>
                          Office Hours
                        </Typography>
                        <Typography sx={{ color: colors.secondary, fontWeight: 500 }}>
                          Mon - Fri: 8am - 5pm EST
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LocationOnIcon sx={{ color: colors.primary, fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, mb: 0.5 }}>
                          Address
                        </Typography>
                        <Typography sx={{ color: colors.secondary, fontWeight: 500 }}>
                          3721 New Court Avenue<br />
                          Syracuse, NY 13206 USA
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sales Team Section */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondary} 50%, ${colors.background} 50%, ${colors.background} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            {/* Left - Outside Sales */}
            <Grid item xs={12} md={6}>
              <Box sx={{ py: { xs: 6, md: 8 }, pr: { md: 6 } }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 700,
                    mb: 4,
                    textAlign: 'center',
                  }}
                >
                  Outside Sales
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', color: '#FFFFFF' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        Keith Sykora
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        sykora@rpmcontrolcorp.com
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        (315) 415-4515
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', color: '#FFFFFF' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        Justin Smith
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        jsmith@processcontrolllc.com
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        (315) 418-8345
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Right - Inside Sales */}
            <Grid item xs={12} md={6}>
              <Box sx={{ py: { xs: 6, md: 8 }, pl: { md: 6 } }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: colors.secondary,
                    fontWeight: 700,
                    mb: 4,
                    textAlign: 'center',
                  }}
                >
                  Inside Sales
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: colors.secondary }}>
                        Rose Saya
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        Rsaya@processcontrolllc.com
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: colors.secondary }}>
                        Justin Smith
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        Jsmith@processcontrolllc.com
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        (315) 418-8345
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                  border: 'none',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 700,
                      mb: 4,
                    }}
                  >
                    Why Choose Us?
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {whyChooseUs.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
                        <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: colors.border,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.secondary,
                      fontWeight: 700,
                      mb: 3,
                    }}
                  >
                    Quick Response
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.9,
                      mb: 3,
                    }}
                  >
                    We understand that downtime costs money. That's why we 
                    respond to all inquiries within 24 hours business hours 
                    and offer expedited service for urgent projects.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.9,
                    }}
                  >
                    At RPM Control & Process Control, we service what we sell—something 
                    you don't get with an online point & click store. We have good 
                    working knowledge of the products we sell and maintain lasting 
                    relationships with our manufacturers.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Snackbar for form submission feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            backgroundColor: snackbar.severity === 'success' ? colors.primary : undefined,
            color: snackbar.severity === 'success' ? '#FFFFFF' : undefined,
            borderRadius: 2,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
